import { useLingui } from '@lingui/react/macro';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router';

import { AccountDropdown } from '@/layout/header/AccountDropdown';
import { getPatientAge } from '@/modules/patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import {
  actionableStatuses,
  allStatuses,
} from '@/modules/patientList/patientStrip/patientStripRedesigned/TreatmentCounts';

import ContactSupportWidget from '../../../app/appHeader/customerSupportWidget/CustomerSupportWidget';
import {
  ProductUpdatesNotifier,
  RenderLogo,
} from '../../../app/appHeader/helpers';
import { selectedVisitAtom } from '../../../app/appHeader/imageViewerMenu/atoms';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { useVisitSummaries } from '../../../react-query/useVisitSummaries';
import { blueBlack700, orange700 } from '../../../theme/videa-style-tokens';
import { computeFindingGroups } from '../../../utils/finding';
import { useIsEmbedded } from '../../../utils/hooks';
import { useAppContext } from '../../../utils/hooks/useAppContext';
import { useIsVDAPracticeWithNonVDAAnalysis } from '../../../utils/hooks/useIsVDAPracticeWithNonVDAAnalysis';
import { colors, layout } from '../../../utils/theme';
import {
  isActionableViewAtom,
  selectUnifiedIdAtom,
} from '../../patientList/atoms';
import { PatientStripOdontogram } from '../../patientList/patientStrip/patientStripOdontogram/patientStripOdontogram';
import type { ImageObject } from '../types';
import { useGetModelFeatureFlags } from '../utils';
import { ViewerRedesignButton } from '../visitSidebar/ViewerRedesignButton';

import { DMFEvaluation } from './dmfEvaluation/DMFEvaluation';
import FindingList from './findingList/FindingList';
import { InsuranceClaimFooter } from './insuranceClaimFooter/InsuranceClaimFooter';
import { AIHasReran, RerunAi } from './RerunAi';
import { computeFindingGroupCategories } from './utils';

const localStyles = makeStyles((theme) => ({
  tab: {
    width: '50%',
    minWidth: '50%',
  },
  findingSidebarContainer: {
    background: blueBlack700,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: layout.FINDINGS_SIDEBAR_WIDTH,
    marginTop: '4em',
  },
  findingSidebarCollapsed: {
    background: '#000',
    width: '100%',
  },
  overviewHeader: {
    padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
    backgroundColor: '#D3F2FF',
  },
  toothNumberLabel: {
    display: 'inline',
    paddingRight: theme.spacing(1),
    color: 'white',
    fontWeight: 500,
  },
  toothNumberLabelContainer: {
    width: '40%',
  },
  imageIndexLabel: {
    textAlign: 'right',
  },
  m1Top: {
    marginTop: theme.spacing(1),
  },
  videaScoreTitle: {
    textTransform: 'uppercase',
    width: '100%',
    verticalAlign: 'middle',
    marginBottom: 5,
  },
  videaScoreInfoIcon: {
    verticalAlign: 'middle',
    marginLeft: 5,
    marginTop: -4,
    fill: '#2293FC',
    width: 24,
    height: 24,
  },
  videaScoreBox: {
    backgroundColor: '#ECF4F6',
    padding: theme.spacing(2),
  },
  videaScoreSelectBox: {
    marginTop: theme.spacing(3),
    width: '100%',
  },
  videaScoreInfoPopover: {
    pointerEvents: 'none',
  },
  findingsHeader: {
    display: 'block',
    width: '100%',
  },
  tuneIcon: {
    marginTop: '1.5em',
    position: 'relative',
    zIndex: 10,
  },
  hiddenFindingsIndicator: {
    marginTop: theme.spacing(5),
    position: 'absolute',
    background: orange700,
    color: orange700,
    left: theme.spacing(1),
    width: theme.spacing(1),
    height: theme.spacing(1),
    borderRadius: '4px',
    zIndex: 10,
  },
  extraPaddingForInsuranceClaimFooter: {
    paddingBottom: '220px',
  },
  findingGroupCategory: {
    width: '100%',
    color: colors.HEADER,
    padding: '1em 1em .5em',
    borderTop: `1px solid black`,
  },
  hiddenFindingsChip: {
    margin: '.5em 1.5em',
    backgroundColor: 'rgba(0,0,0,0.2)',
    color: orange700,
    borderRadius: '4px',
    fontSize: '0.875em',
  },
  ddaiHeader: {
    position: 'absolute',
    top: '0',
    right: '0',
    zIndex: 100,
    backgroundColor: blueBlack700,
    width: '20.5em',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0.5em 1em',
    '& img': {
      width: '14em',
      transform: 'translateY(0.25em)',
    },
  },
  videaAiHeader: {
    position: 'absolute',
    top: '0',
    right: '0',
    zIndex: 100,
    backgroundColor: blueBlack700,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0.5em 1em',
    gap: '0.5em',
    '& .logo': {
      display: 'flex',
      width: '113px',
      height: '50px',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
}));

const FindingSidebar = (props: any) => {
  const {
    enabledFindingTypes,
    systemFindings,
    isFullScreenViewer,
    setAnchorEls,
    userAnnotations,
    viewerMode,
    imageAnalyses,
    sortedAndFilteredImages,
    currentlyHoveredImageId,
    user,
    isInsuranceClaimLoading,
    isSidebarCollapsed,
    isShouldDisplayRootTips,
    onChangeViewerMode,
    onDeleteUserAnnotation,
    onHandleSetAnnotationDrawType,
    onHighlightFinding,
    onSetCurrentlyHoveredImageId,
    onSetEnabledFindingTypes,
    onSetShouldDisplayRootTips,
    onSetIsRblMeasurementGuideOpen,
    patientInfo,
    onToggleIsInsuranceClaimPdfOpen,
    onUpdateFinding,
    onUpdateToothNumber,
  } = props;
  const activeImage: undefined | ImageObject = props.activeImage;
  const classes = localStyles();
  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
  } = useGetModelFeatureFlags();
  const { isAuthenticated } = useAppContext();
  const { t } = useLingui();
  const isActionableView = useAtomValue(isActionableViewAtom);

  type FindingCategory = 'Restorative' | 'Periodontal' | 'Endodontic';

  const isFindingCategory = (value: string): value is FindingCategory => {
    return ['Restorative', 'Periodontal', 'Endodontic'].includes(value);
  };

  const categoryToNames: Record<FindingCategory, string> = {
    Restorative: t`Restorative`,
    Periodontal: t`Periodontal`,
    Endodontic: t`Endodontic`,
  };

  const featureFlaggedFindingTypes = {
    caries: true,
    periapical_radiolucency: isPrlEnabled,
    radiographic_boneloss: isRblEnabled,
    calculus: isCalculusEnabled,
    area_of_concern: isCariesSubclassEnabled,
    restorative_adjacent_lucency: isVdaEnabled,
  };
  const findingGroupsToRender = computeFindingGroups(
    userAnnotations,
    systemFindings,
    featureFlaggedFindingTypes,
  );
  const isEmbedded = useIsEmbedded();

  const findingGroupCategories = computeFindingGroupCategories(
    findingGroupsToRender,
  );

  const isRestorative = (category: any) =>
    category?.toLocaleLowerCase() === 'restorative';

  const { id: selectedPatientId } = useParams<{ id: string }>();
  const isVDAPracticeWithNonVDAAnalysis = useIsVDAPracticeWithNonVDAAnalysis(
    activeImage?.imageId ? [activeImage] : sortedAndFilteredImages,
  );

  const estimatedVisitIdAtomValue: string | null =
    useAtomValue(selectedVisitAtom);
  const unifiedAppointmentId = useAtomValue(selectUnifiedIdAtom);
  const estimatedVisitId = estimatedVisitIdAtomValue
    ? parseInt(estimatedVisitIdAtomValue)
    : sortedAndFilteredImages[0]?.estimatedVisitId;
  const visitSummariesData = useVisitSummaries({
    visits: [
      {
        estimatedVisitId,
        unifiedAppointmentId: unifiedAppointmentId
          ? parseInt(unifiedAppointmentId)
          : undefined,
      },
    ],
  });
  const visitSummariesPages = visitSummariesData?.map((visitSummaryData) => {
    if (Array.isArray(visitSummaryData?.data)) {
      return visitSummaryData?.data?.map((data) => data);
    }
  });
  const visitSummaries = visitSummariesPages?.flat();
  return (
    <>
      <Box
        width={1}
        height={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        className={
          !isSidebarCollapsed
            ? classes.findingSidebarContainer
            : classes.findingSidebarCollapsed
        }
      >
        {!isSidebarCollapsed && (
          <>
            <Box
              width={1}
              flexDirection="column"
              className={classes.extraPaddingForInsuranceClaimFooter}
            >
              {isEmbedded && (
                <div className={classes.ddaiHeader}>
                  <img
                    src="/assets/images/ddai/dentrix-videaai.svg"
                    alt="Dentrix Videa Logo"
                  />
                  <ProductUpdatesNotifier />
                  {!!isAuthenticated && <AccountDropdown />}
                </div>
              )}
              {!isEmbedded && (
                <div className={classes.videaAiHeader}>
                  <RenderLogo />
                  <ProductUpdatesNotifier />
                  <ContactSupportWidget />
                  {!!isAuthenticated && <AccountDropdown />}
                </div>
              )}

              {isVDAPracticeWithNonVDAAnalysis ? (
                <RerunAi
                  estimatedVisitId={estimatedVisitId}
                  images={sortedAndFilteredImages}
                  selectedPatientId={selectedPatientId}
                />
              ) : (
                <AIHasReran />
              )}

              <ViewerRedesignButton />

              <PatientStripOdontogram
                age={getPatientAge(patientInfo?.dob)}
                selectedStatuses={
                  isActionableView ? actionableStatuses : allStatuses
                }
                visitSummary={visitSummaries?.[0]}
                module="image-viewer"
              />
              <>
                {Object.keys(findingGroupCategories)
                  .filter(isFindingCategory)
                  .map((category) => (
                    <div key={category}>
                      <Box display="flex" alignItems="center">
                        <VideaTypography
                          variant="headingExtrasmall"
                          className={classes.findingGroupCategory}
                        >
                          {categoryToNames[category as FindingCategory]}
                        </VideaTypography>
                      </Box>
                      {activeImage?.imageId && isRestorative(category) && (
                        <DMFEvaluation imageId={activeImage?.imageId} />
                      )}
                      <>
                        {findingGroupCategories[
                          category as FindingCategory
                        ].map((findingGroup: any, index: any) => (
                          <FindingList
                            key={index}
                            onChangeViewerMode={onChangeViewerMode}
                            onHandleSetAnnotationDrawType={
                              onHandleSetAnnotationDrawType
                            }
                            onDeleteUserAnnotation={onDeleteUserAnnotation}
                            enabledFindingTypes={enabledFindingTypes}
                            findingGroup={findingGroup}
                            onHighlightFinding={onHighlightFinding}
                            onSetEnabledFindingTypes={onSetEnabledFindingTypes}
                            setAnchorEls={setAnchorEls}
                            // @ts-expect-error TS(2322): Type '{ key: any; changeViewerMode: any; handleSet... Remove this comment to see the full error message
                            isFullScreenViewer={isFullScreenViewer}
                            onUpdateFinding={onUpdateFinding}
                            viewerMode={viewerMode}
                            onUpdateToothNumber={onUpdateToothNumber}
                            imageAnalyses={imageAnalyses}
                            sortedAndFilteredImages={sortedAndFilteredImages}
                            activeImage={activeImage}
                            onSetCurrentlyHoveredImageId={
                              onSetCurrentlyHoveredImageId
                            }
                            currentlyHoveredImageId={currentlyHoveredImageId}
                            user={user}
                            isShouldDisplayRootTips={isShouldDisplayRootTips}
                            onSetShouldDisplayRootTips={
                              onSetShouldDisplayRootTips
                            }
                            featureFlaggedFindingTypes={
                              featureFlaggedFindingTypes
                            }
                            onSetIsRblMeasurementGuideOpen={
                              onSetIsRblMeasurementGuideOpen
                            }
                          />
                        ))}
                      </>
                    </div>
                  ))}
              </>
            </Box>
            {!isEmbedded && (
              <InsuranceClaimFooter
                patientInfo={patientInfo}
                estimatedVisitId={estimatedVisitId}
                isLoading={isInsuranceClaimLoading}
                onToggleIsInsuranceClaimPdfOpen={
                  onToggleIsInsuranceClaimPdfOpen
                }
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default FindingSidebar;
